import * as React from 'react';

/**
 * One page of a list the hub document carries whole — its responses and its notes, which
 * survey-api sends **unpaged**, bounded by the household's account links (person hub design
 * §4: "the hub's lists page client-side"). Cut at a record page's size (`useListPaging`, ten,
 * "Show more" for ten more) under the shared `Pager`,
 * so a page of them reads as a page of any admin list does.
 *
 * Paged here rather than drawn whole: a migrated person with years of responses and notes is a
 * long list, and every other admin screen pages one. Asking survey-api for a page instead was
 * the participant card's way, and it was three reads of three moments.
 *
 * **The page is never clamped.** A re-read can shrink the list under an open later page — an
 * unlink takes a member's responses and notes with it — and the page then holds nothing of a
 * list that is not empty. Each caller says so with `PastTheEnd`, as every admin list does, rather
 * than its empty sentence; turning the page back silently would move a reader without a word.
 */
export function useClientPage<T>(items: T[]): {
  rows: T[];
  page: number;
  setPage: (page: number) => void;
  /** "Show more": ten more rows on the page (`useListPaging`). */
  more: () => void;
  pager: { page: number; pageSize: number; total: number };
} {
  const { page, pageSize, setPage, more } = useListPaging();
  const start = (page - 1) * pageSize;
  return {
    rows: items.slice(start, start + pageSize),
    page,
    setPage,
    more,
    pager: { page, pageSize, total: items.length }
  };
}

/** A record page's lists start at ten rows, Salesforce's related lists' size (SR-407); the
 *  admin lists' 50 drew a page of a feed taller than the rest of the record. */
export const RECORD_PAGE_SIZE = 10;

/**
 * The page and its size, for a list on the record page: ten rows, a pager, and "Show more", which
 * makes the page ten rows longer — keeping the first row in view, so the reader is not moved — and
 * which survey-api's paging takes as it takes any size (SR-407).
 */
/** "Show more": the page `step` rows longer, up to `max`, and turned so its first row stays in
 *  view. */
export function grownPage(
  { page, pageSize }: { page: number; pageSize: number },
  step: number,
  max = Number.POSITIVE_INFINITY
): { page: number; pageSize: number } {
  const size = Math.min(pageSize + step, max);
  return { page: Math.floor(((page - 1) * pageSize) / size) + 1, pageSize: size };
}

export function useListPaging(): {
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  more: () => void;
} {
  const [{ page, pageSize }, setPaging] = React.useState({ page: 1, pageSize: RECORD_PAGE_SIZE });
  const setPage = React.useCallback(
    (next: number) => setPaging((now) => ({ ...now, page: next })),
    []
  );
  const more = React.useCallback(() => setPaging((now) => grownPage(now, RECORD_PAGE_SIZE)), []);
  return { page, pageSize, setPage, more };
}
